/**
 * Person tags — multi-select bottom sheet. Purely practical metadata:
 * questions get tagged, the deck can be filtered by tag. Nothing else.
 */

import { useState } from "react";
import type { Person } from "../lib/persons";

export function TagSheet({
  persons,
  selected,
  busy,
  onApply,
  onClose,
}: {
  persons: Person[];
  selected: string[];
  busy?: boolean;
  onApply: (ids: string[]) => void;
  onClose: () => void;
}) {
  const [ids, setIds] = useState<string[]>(selected);
  const toggle = (id: string) =>
    setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="fixed inset-0 z-40 flex items-end bg-ink-950/60" onClick={onClose}>
      <div
        className="card w-full rounded-b-none p-4 pb-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium text-ink-200">
            Person tags <span className="text-ink-500">(for filtering only)</span>
          </span>
          <button className="btn-ghost px-3 py-1.5 text-xs" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="space-y-2">
          {persons.map((p) => {
            const on = ids.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition-colors ${
                  on ? "border-accent/40 bg-accent/5" : "border-ink-800 hover:bg-ink-850"
                }`}
              >
                <span className="text-sm text-ink-200">
                  {p.name}
                  {p.role ? <span className="text-ink-500"> · {p.role}</span> : null}
                </span>
                <span className={`text-sm ${on ? "text-accent-soft" : "text-ink-600"}`}>
                  {on ? "✓" : "○"}
                </span>
              </button>
            );
          })}
        </div>
        <button
          className="btn-primary mt-3 w-full py-2 text-xs"
          disabled={busy}
          onClick={() => onApply(ids)}
        >
          {busy ? "saving…" : "Apply"}
        </button>
      </div>
    </div>
  );
}
